import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Sparkles, 
  Globe, 
  Clock, 
  Shield, 
  TrendingUp, 
  Users, 
  Zap,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Sparkles,
      title: 'AI-Powered Questions',
      description: 'Dynamic questions generated by Groq AI that adapt to candidate responses'
    },
    {
      icon: Globe,
      title: 'Resume Parsing',
      description: 'Intelligent resume analysis using Google Gemini AI for personalized interviews'
    },
    {
      icon: Clock,
      title: '24/7 Availability',
      description: 'Candidates can interview anytime, anywhere at their convenience'
    },
    {
      icon: Shield,
      title: 'Anti-Cheating',
      description: 'Advanced integrity monitoring ensures authentic assessments'
    },
    {
      icon: TrendingUp,
      title: 'Detailed Analytics',
      description: 'Comprehensive performance metrics and skill assessments'
    },
    {
      icon: Users,
      title: 'Resume Analysis',
      description: 'AI parses resumes and generates tailored interview questions'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Create Campaign',
      description: 'Set up your hiring campaign with job requirements'
    },
    {
      step: '02',
      title: 'Invite Candidates',
      description: 'Send interview links to candidates via email'
    },
    {
      step: '03',
      title: 'AI Interviews',
      description: 'AI conducts multi-turn conversations with candidates'
    },
    {
      step: '04',
      title: 'Review Results',
      description: 'Get detailed reports and performance analytics'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Zap className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-bold text-slate-900">My Interview AI</span>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-slate-600 hover:text-indigo-600 transition-colors">Features</a>
            <a href="#how-it-works" className="text-slate-600 hover:text-indigo-600 transition-colors">How it Works</a>
            <a href="#pricing" className="text-slate-600 hover:text-indigo-600 transition-colors">Pricing</a>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="ghost" onClick={() => navigate('/login')}>
              Sign In
            </Button>
            <Button onClick={() => navigate('/signup')} className="bg-indigo-600 hover:bg-indigo-700">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Free Forever for Recruiters</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-tight">
            AI-Powered
            <br />
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Talent Interviews
            </span>
          </h1>
          <p className="text-xl text-slate-600 mb-10 max-w-3xl mx-auto">
            Transform your hiring process with AI-driven video interviews. Dynamic questions, 
            real-time analysis, and comprehensive candidate insights - completely free.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Button 
              size="lg" 
              onClick={() => navigate('/signup')} 
              className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8 py-6 group"
            >
              Start Hiring for Free
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => navigate('/demo-interview')} 
              className="text-lg px-8 py-6"
            >
              Try Demo Interview
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Powerful Features</h2>
            <p className="text-xl text-slate-600">Everything you need for modern recruitment</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={index} 
                  className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white border-slate-200"
                >
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-xl text-slate-600">Simple, fast, and effective</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-slate-600">Free forever for recruiters</p>
          </div>
          <Card className="p-12 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-slate-900 mb-4">Free for Recruiters</h3>
              <div className="text-5xl font-bold text-indigo-600 mb-6">
                $0<span className="text-2xl text-slate-600">/month</span>
              </div>
              <ul className="space-y-4 mb-8 text-left max-w-md mx-auto">
                {[
                  'Unlimited candidates',
                  'AI-powered interviews',
                  'Resume parsing & analysis',
                  'Detailed performance reports',
                  'Multi-language support',
                  '24/7 interview availability',
                  'Anti-cheating detection',
                  'Dashboard & analytics'
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                size="lg" 
                onClick={() => navigate('/signup')} 
                className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8 py-6"
              >
                Start Free Now
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Hiring?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Join hundreds of companies using AI-powered interviews
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/signup')} 
            className="bg-white text-indigo-600 hover:bg-slate-100 text-lg px-8 py-6"
          >
            Get Started for Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Zap className="w-6 h-6 text-indigo-400" />
                <span className="text-xl font-bold text-white">My Interview AI</span>
              </div>
              <p className="text-sm">AI-powered talent interviews for modern recruitment.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-indigo-400 transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-indigo-400 transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-indigo-400 transition-colors">Demo</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-indigo-400 transition-colors">About</a></li>
                <li><a href="#" className="hover:text-indigo-400 transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-indigo-400 transition-colors">Privacy</a></li>
                <li><a href="#" className="hover:text-indigo-400 transition-colors">Terms</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>&copy; 2025 My Interview AI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;